import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Typography,
  Chip,
  IconButton,
  Alert,
  Grid,
  InputAdornment,
  Tabs,
  Tab,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Settings as SettingsIcon,
  Business as FacilitiesIcon,
  People as PeopleIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import FacilityAccessEditor from './FacilityAccessEditor';
import api from '../utils/api';

const PlanForm = ({ plan, onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
    features: [''],
    facilityAccess: [],
    memberTypeConfig: [],
    defaultEligible: true,
    isActive: true,
    freezable: false,
    maxFreezeDays: 0,
    allowInstantNextPlanActivation: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [facilityAccessValid, setFacilityAccessValid] = useState(true);
  const [memberTypes, setMemberTypes] = useState([]);

  useEffect(() => {
    loadMemberTypes();
    
    if (plan) {
      setFormData({
        name: plan.name || '',
        description: plan.description || '',
        price: plan.price?.toString() || '',
        duration: plan.duration?.toString() || '',
        features: plan.features?.length > 0 ? [...plan.features] : [''],
        facilityAccess: plan.facilityAccess || [],
        memberTypeConfig: plan.memberTypeConfig || [],
        defaultEligible: plan.defaultEligible !== undefined ? plan.defaultEligible : true,
        isActive: plan.isActive !== undefined ? plan.isActive : true,
        freezable: plan.freezable || false,
        maxFreezeDays: plan.maxFreezeDays || 0,
        allowInstantNextPlanActivation: plan.allowInstantNextPlanActivation || false
      });
    }
  }, [plan]);

  const loadMemberTypes = async () => {
    try {
      const response = await api.get('/member-types');
      setMemberTypes(response.data.data);
    } catch (error) {
      console.error('Error loading member types:', error);
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Plan name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'Plan name cannot exceed 100 characters';
    }

    // Description validation
    if (!formData.description.trim()) {
      newErrors.description = 'Plan description is required';
    } else if (formData.description.length > 500) {
      newErrors.description = 'Plan description cannot exceed 500 characters';
    }

    // Price validation
    const price = parseFloat(formData.price);
    if (!formData.price || isNaN(price)) {
      newErrors.price = 'Valid price is required';
    } else if (price < 0) {
      newErrors.price = 'Price cannot be negative';
    }

    // Duration validation
    const duration = parseInt(formData.duration);
    if (!formData.duration || isNaN(duration)) {
      newErrors.duration = 'Valid duration is required';
    } else if (duration < 1) {
      newErrors.duration = 'Duration must be at least 1 day';
    } else if (duration > 365) {
      newErrors.duration = 'Duration cannot exceed 365 days';
    }

    // Features validation
    const validFeatures = formData.features.filter(feature => feature.trim());
    if (validFeatures.length === 0) {
      newErrors.features = 'At least one feature is required';
    } else {
      const invalidFeatures = formData.features.some(feature => 
        feature.trim() && feature.length > 200
      );
      if (invalidFeatures) {
        newErrors.features = 'Feature descriptions cannot exceed 200 characters';
      }
    }

    setErrors(newErrors);
    const hasBasicErrors = Object.keys(newErrors).length > 0;
    return !hasBasicErrors && facilityAccessValid;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...formData.features];
    newFeatures[index] = value;
    setFormData(prev => ({
      ...prev,
      features: newFeatures
    }));
    
    // Clear features error
    if (errors.features) {
      setErrors(prev => ({
        ...prev,
        features: ''
      }));
    }
  };

  const addFeature = () => {
    setFormData(prev => ({
      ...prev,
      features: [...prev.features, '']
    }));
  };

  const removeFeature = (index) => {
    if (formData.features.length > 1) {
      const newFeatures = formData.features.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        features: newFeatures
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        duration: parseInt(formData.duration),
        features: formData.features.filter(feature => feature.trim()),
        facilityAccess: formData.facilityAccess,
        memberTypeConfig: formData.memberTypeConfig,
        defaultEligible: formData.defaultEligible,
        freezable: formData.freezable,
        maxFreezeDays: parseInt(formData.maxFreezeDays) || 0,
        allowInstantNextPlanActivation: formData.allowInstantNextPlanActivation
      };

      await onSubmit(submitData);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDurationHelper = (days) => {
    if (!days) return '';
    const duration = parseInt(days);
    if (isNaN(duration)) return '';
    
    if (duration === 1) return '1 day';
    if (duration < 30) return `${duration} days`;
    if (duration === 30) return '1 month';
    if (duration < 365) return `${Math.round(duration / 30)} months`;
    return `${Math.round(duration / 365)} year(s)`;
  };

  const handleFacilityAccessChange = (newFacilityAccess) => {
    setFormData(prev => ({
      ...prev,
      facilityAccess: newFacilityAccess
    }));
  };

  const handleFacilityAccessValidation = (isValid) => {
    setFacilityAccessValid(isValid);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Member type configuration helpers
  const addMemberTypeConfig = () => {
    const availableMemberTypes = memberTypes.filter(mt => 
      !formData.memberTypeConfig.find(config => config.memberType === mt._id)
    );
    
    if (availableMemberTypes.length > 0) {
      const newConfig = {
        memberType: availableMemberTypes[0]._id,
        isEligible: true,
        priceModifier: {
          type: 'fixed',
          value: null
        },
        customFeatures: [],
        restrictions: {
          maxUsersAllowed: null,
          requiresApproval: false
        }
      };
      
      setFormData(prev => ({
        ...prev,
        memberTypeConfig: [...prev.memberTypeConfig, newConfig]
      }));
    }
  };

  const removeMemberTypeConfig = (index) => {
    setFormData(prev => ({
      ...prev,
      memberTypeConfig: prev.memberTypeConfig.filter((_, i) => i !== index)
    }));
  };

  const updateMemberTypeConfig = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      memberTypeConfig: prev.memberTypeConfig.map((config, i) => 
        i === index ? { ...config, [field]: value } : config
      )
    }));
  };

  const updateMemberTypeConfigNested = (index, path, value) => {
    setFormData(prev => ({
      ...prev,
      memberTypeConfig: prev.memberTypeConfig.map((config, i) => {
        if (i !== index) return config;
        
        const pathArray = path.split('.');
        const newConfig = { ...config };
        let current = newConfig;
        
        for (let j = 0; j < pathArray.length - 1; j++) {
          current[pathArray[j]] = { ...current[pathArray[j]] };
          current = current[pathArray[j]];
        }
        current[pathArray[pathArray.length - 1]] = value;
        
        return newConfig;
      })
    }));
  };

  const addCustomFeature = (configIndex) => {
    updateMemberTypeConfig(configIndex, 'customFeatures', [
      ...formData.memberTypeConfig[configIndex].customFeatures,
      ''
    ]);
  };

  const updateCustomFeature = (configIndex, featureIndex, value) => {
    const newFeatures = [...formData.memberTypeConfig[configIndex].customFeatures];
    newFeatures[featureIndex] = value;
    updateMemberTypeConfig(configIndex, 'customFeatures', newFeatures);
  };

  const removeCustomFeature = (configIndex, featureIndex) => {
    const newFeatures = formData.memberTypeConfig[configIndex].customFeatures.filter(
      (_, i) => i !== featureIndex
    );
    updateMemberTypeConfig(configIndex, 'customFeatures', newFeatures);
  };

  const renderBasicTab = () => (
    <Grid container spacing={3} sx={{ width: '100%', mt: 1 }}>
      <Grid size={{ xs: 12, sm: 6 }}>
        <TextField
            fullWidth
            label="Plan Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
          />
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Price"
            type="number"
            value={formData.price}
            onChange={(e) => handleInputChange('price', e.target.value)}
            error={!!errors.price}
            helperText={errors.price}
            required
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
          />
        </Grid>

        <Grid size={12}>
          <TextField
            fullWidth
            label="Description"
            multiline
            rows={3}
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            error={!!errors.description}
            helperText={errors.description}
            required
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            fullWidth
            label="Duration (days)"
            type="number"
            value={formData.duration}
            onChange={(e) => handleInputChange('duration', e.target.value)}
            error={!!errors.duration}
            helperText={errors.duration || formatDurationHelper(formData.duration)}
            required
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={(e) => handleInputChange('isActive', e.target.checked)}
              />
            }
            label="Active Plan"
          />
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Plan Freeze Settings
          </Typography>
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.freezable}
                onChange={(e) => {
                  handleInputChange('freezable', e.target.checked);
                  if (!e.target.checked) {
                    handleInputChange('maxFreezeDays', 0);
                  }
                }}
              />
            }
            label="Freezable Plan"
          />
          <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4 }}>
            Allow users to temporarily freeze this plan
          </Typography>
        </Grid>

        {formData.freezable && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              fullWidth
              label="Maximum Freeze Days"
              type="number"
              value={formData.maxFreezeDays}
              onChange={(e) => handleInputChange('maxFreezeDays', parseInt(e.target.value) || 0)}
              helperText="Total days this plan can be frozen (e.g., 30 for 6-month, 60 for 12-month)"
              inputProps={{ min: 0, max: 365 }}
            />
          </Grid>
        )}

        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Next Plan Activation Settings
          </Typography>
        </Grid>

        <Grid size={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.allowInstantNextPlanActivation}
                onChange={(e) => handleInputChange('allowInstantNextPlanActivation', e.target.checked)}
              />
            }
            label="Allow Instant Next Plan Activation"
          />
          <Typography variant="caption" display="block" color="text.secondary" sx={{ ml: 4 }}>
            When enabled, users with this plan can instantly replace it by purchasing any next plan. Their current plan will be immediately replaced upon purchase.
          </Typography>
          {formData.allowInstantNextPlanActivation && (
            <Alert severity="warning" sx={{ mt: 1, ml: 4 }}>
              ⚠️ Warning: Users will lose any remaining time on their current plan when purchasing a new plan.
            </Alert>
          )}
        </Grid>

        <Grid size={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="h6" gutterBottom>
            Features
          </Typography>
          {errors.features && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.features}
            </Alert>
          )}
          
          {formData.features.map((feature, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <TextField
                fullWidth
                label={`Feature ${index + 1}`}
                value={feature}
                onChange={(e) => handleFeatureChange(index, e.target.value)}
                placeholder="Enter feature description"
              />
              <IconButton
                onClick={() => removeFeature(index)}
                disabled={formData.features.length === 1}
                color="error"
                sx={{ ml: 1 }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ))}
          
          <Button
            startIcon={<AddIcon />}
            onClick={addFeature}
            variant="outlined"
            size="small"
          >
            Add Feature
          </Button>
        </Grid>

    </Grid>
  );

  const renderMemberTypeAccessTab = () => (
    <Box sx={{ mt: 1 }}>
      <Typography variant="h6" gutterBottom>
        Member Type Access Configuration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure which member types can access this plan and set pricing or feature modifications.
      </Typography>

      {/* Default Eligibility */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Default Access Policy
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.defaultEligible}
                onChange={(e) => handleInputChange('defaultEligible', e.target.checked)}
              />
            }
            label="Allow access for member types not explicitly configured"
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            When enabled, member types without specific configuration will be able to access this plan.
          </Typography>
        </CardContent>
      </Card>

      {/* Member Type Configurations */}
      <Typography variant="h6" gutterBottom>
        Member Type Specific Settings
      </Typography>
      
      {formData.memberTypeConfig.map((config, configIndex) => {
        const memberType = memberTypes.find(mt => mt._id === config.memberType);
        
        return (
          <Accordion key={configIndex} sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Typography variant="h6">
                  {memberType?.name || 'Unknown Member Type'}
                </Typography>
                <Chip 
                  label={config.isEligible ? 'Eligible' : 'Not Eligible'}
                  color={config.isEligible ? 'success' : 'error'}
                  size="small"
                />
                {(() => {
                  const hasCustomPricing = 
                    config.priceModifier?.type === 'percentage' ||
                    config.priceModifier?.type === 'discount' ||
                    (config.priceModifier?.type === 'fixed' && config.priceModifier?.value !== null && config.priceModifier?.value !== undefined);
                  
                  return hasCustomPricing ? (
                    <Chip 
                      label="Custom Pricing" 
                      color="info" 
                      size="small"
                    />
                  ) : null;
                })()}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={3}>
                {/* Member Type Selection */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth>
                    <InputLabel>Member Type</InputLabel>
                    <Select
                      value={config.memberType}
                      onChange={(e) => updateMemberTypeConfig(configIndex, 'memberType', e.target.value)}
                    >
                      {memberTypes.map(mt => (
                        <MenuItem key={mt._id} value={mt._id}>
                          {mt.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Eligibility */}
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={config.isEligible}
                        onChange={(e) => updateMemberTypeConfig(configIndex, 'isEligible', e.target.checked)}
                      />
                    }
                    label="Allow access for this member type"
                  />
                </Grid>

                {config.isEligible && (
                  <>
                    {/* Price Modifier */}
                    <Grid size={12}>
                      <Typography variant="subtitle1" gutterBottom>
                        Pricing Configuration
                      </Typography>
                    </Grid>
                    
                    <Grid size={{ xs: 12, sm: 4 }}>
                      <FormControl fullWidth>
                        <InputLabel>Price Type</InputLabel>
                        <Select
                          value={config.priceModifier?.type || 'fixed'}
                          onChange={(e) => updateMemberTypeConfigNested(configIndex, 'priceModifier.type', e.target.value)}
                        >
                          <MenuItem value="fixed">Fixed Price</MenuItem>
                          <MenuItem value="percentage">Percentage Multiplier</MenuItem>
                          <MenuItem value="discount">Discount Amount</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                      <TextField
                        fullWidth
                        label={
                          config.priceModifier?.type === 'percentage' 
                            ? 'Multiplier (1.0 = 100%)'
                            : config.priceModifier?.type === 'discount'
                            ? 'Discount Amount'
                            : 'Fixed Price'
                        }
                        placeholder={
                          config.priceModifier?.type === 'percentage' 
                            ? '1.0'
                            : config.priceModifier?.type === 'discount'
                            ? '0'
                            : 'Leave empty to use base price'
                        }
                        type="number"
                        value={config.priceModifier?.value !== null && config.priceModifier?.value !== undefined ? config.priceModifier.value : ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          const numValue = value === '' ? null : parseFloat(value);
                          updateMemberTypeConfigNested(configIndex, 'priceModifier.value', numValue);
                        }}
                        InputProps={{
                          startAdornment: config.priceModifier?.type !== 'percentage' ? <InputAdornment position="start">$</InputAdornment> : null,
                        }}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 4 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ pt: 1 }}>
                        {(() => {
                          const basePrice = parseFloat(formData.price) || 0;
                          const modifier = config.priceModifier || { type: 'fixed', value: null };
                          let calculatedPrice = basePrice;
                          
                          if (modifier.type === 'fixed') {
                            calculatedPrice = modifier.value !== null && modifier.value !== undefined ? modifier.value : basePrice;
                          } else if (modifier.type === 'percentage') {
                            calculatedPrice = basePrice * (modifier.value !== null && modifier.value !== undefined ? modifier.value : 1);
                          } else if (modifier.type === 'discount') {
                            calculatedPrice = Math.max(0, basePrice - (modifier.value || 0));
                          }
                          
                          return `Final price: $${calculatedPrice.toFixed(2)}`;
                        })()}
                      </Typography>
                    </Grid>

                    {/* Custom Features */}
                    <Grid size={12}>
                      <Typography variant="subtitle1" gutterBottom>
                        Custom Features for {memberType?.name}
                      </Typography>
                      {config.customFeatures?.map((feature, featureIndex) => (
                        <Box key={featureIndex} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder="Enter custom feature"
                            value={feature}
                            onChange={(e) => updateCustomFeature(configIndex, featureIndex, e.target.value)}
                          />
                          <IconButton
                            onClick={() => removeCustomFeature(configIndex, featureIndex)}
                            color="error"
                            sx={{ ml: 1 }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                      <Button
                        startIcon={<AddIcon />}
                        onClick={() => addCustomFeature(configIndex)}
                        variant="outlined"
                        size="small"
                      >
                        Add Custom Feature
                      </Button>
                    </Grid>

                    {/* Restrictions */}
                    <Grid size={12}>
                      <Typography variant="subtitle1" gutterBottom>
                        Access Restrictions
                      </Typography>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <TextField
                        fullWidth
                        label="Max Users Allowed (blank = unlimited)"
                        type="number"
                        value={config.restrictions?.maxUsersAllowed || ''}
                        onChange={(e) => updateMemberTypeConfigNested(configIndex, 'restrictions.maxUsersAllowed', e.target.value ? parseInt(e.target.value) : null)}
                      />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={config.restrictions?.requiresApproval || false}
                            onChange={(e) => updateMemberTypeConfigNested(configIndex, 'restrictions.requiresApproval', e.target.checked)}
                          />
                        }
                        label="Require admin approval"
                      />
                    </Grid>
                  </>
                )}

                {/* Remove Configuration Button */}
                <Grid size={12}>
                  <Divider sx={{ mb: 2 }} />
                  <Button
                    startIcon={<DeleteIcon />}
                    onClick={() => removeMemberTypeConfig(configIndex)}
                    color="error"
                    variant="outlined"
                    size="small"
                  >
                    Remove Member Type Configuration
                  </Button>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Add Member Type Button */}
      <Button
        startIcon={<AddIcon />}
        onClick={addMemberTypeConfig}
        variant="contained"
        disabled={formData.memberTypeConfig.length >= memberTypes.length}
      >
        Add Member Type Configuration
      </Button>
      
      {formData.memberTypeConfig.length >= memberTypes.length && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          All member types have been configured.
        </Typography>
      )}
    </Box>
  );

  const renderFacilityAccessTab = () => (
    <Box sx={{ mt: 2 }}>
      <FacilityAccessEditor
        facilityAccess={formData.facilityAccess}
        onChange={handleFacilityAccessChange}
        onValidate={handleFacilityAccessValidation}
      />
    </Box>
  );

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Paper sx={{ width: '100%' }}>
        <Tabs value={activeTab} onChange={handleTabChange} aria-label="plan configuration tabs">
          <Tab 
            icon={<SettingsIcon />} 
            label="Basic Settings" 
            iconPosition="start"
            sx={{ minHeight: 60 }}
          />
          <Tab 
            icon={<PeopleIcon />} 
            label="Member Type Access" 
            iconPosition="start"
            sx={{ minHeight: 60 }}
          />
          <Tab 
            icon={<FacilitiesIcon />} 
            label={`Facility Access${!facilityAccessValid ? ' (Errors)' : ''}`}
            iconPosition="start"
            sx={{ 
              minHeight: 60,
              color: !facilityAccessValid ? 'error.main' : undefined
            }}
          />
        </Tabs>
        
        <Box sx={{ p: 3 }}>
          {activeTab === 0 && renderBasicTab()}
          {activeTab === 1 && renderMemberTypeAccessTab()}
          {activeTab === 2 && renderFacilityAccessTab()}
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 3, pt: 0 }}>
          <Box>
            {!facilityAccessValid && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Please fix facility access configuration errors before saving.
              </Alert>
            )}
          </Box>
          
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button onClick={onCancel} disabled={loading}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={loading || !facilityAccessValid}
            >
              {loading ? 'Saving...' : (plan ? 'Update Plan' : 'Create Plan')}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default PlanForm;